// userActions.js
import UserService from '../../services/userService';
import { parseAuthError } from '../../services/authService';

// Clear error
export const clearError = () => (dispatch) => {
  dispatch({ type: 'CLEAR_USER_ERROR' });
};

// Register user
export const registerUser = (userData) => async (dispatch) => {
  try {
    dispatch({ type: 'USER_REGISTER_REQUEST' });
    
    const response = await UserService.register(userData);
    
    dispatch({
      type: 'USER_REGISTER_SUCCESS',
      payload: {
        token: response.token,
        user: response.user
      }
    });
    
    return { success: true };
  } catch (error) {
    const errorPayload = {
      message: error.message,
      fieldErrors: {}
    };
    
    // Try to parse structured error responses
    try {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        errorPayload.message = errorData.message || error.message;
        
        // Display errors as toast notifications
        if (errorData.errors && Array.isArray(errorData.errors)) {
          // Show each error as a separate toast notification
          errorData.errors.forEach((err, index) => {
            if (err.message) {
              // Stagger toast notifications slightly for better UX
              setTimeout(() => {
                if (window.toast) {
                  window.toast.error(err.message, 6000);
                }
              }, index * 100); // 100ms delay between each toast
            }
            
            // Map field-specific errors for form validation
            if (err.field && err.message) {
              errorPayload.fieldErrors[err.field] = err.message;
            }
          });
        } else if (errorPayload.message) {
          // If no structured errors, show the general error message
          if (window.toast) {
            window.toast.error(errorPayload.message, 6000);
          }
        }
      } else {
        // Fallback for non-API errors
        if (window.toast) {
          window.toast.error(errorPayload.message || 'Registration failed. Please try again.', 6000);
        }
      }
    } catch (parseError) {
      console.warn('Could not parse error response:', parseError);
      if (window.toast) {
        window.toast.error('An unexpected error occurred. Please try again.', 6000);
      }
    }
    
    dispatch({
      type: 'USER_REGISTER_FAIL',
      payload: errorPayload
    });
    throw error;
  }
};

// Login user
export const loginUser = (credentials) => async (dispatch) => {
  try {
    dispatch({ type: 'USER_LOGIN_REQUEST' });
    
    const response = await UserService.login(credentials);
    
    dispatch({
      type: 'USER_LOGIN_SUCCESS',
      payload: {
        token: response.token,
        user: response.user
      }
    });
    
    return { success: true };
  } catch (error) {
    const { message: errorMessage } = parseAuthError(error);

    if (window.toast) {
      window.toast.error(errorMessage, 6000);
    }

    dispatch({
      type: 'USER_LOGIN_FAIL',
      payload: errorMessage
    });
    throw error;
  }
};

// Logout user
export const logoutUser = () => async (dispatch) => {
  console.log("Logout action dispatched");
  
  // Clear localStorage first
  UserService.logout();
  
  // Dispatch logout action
  dispatch({
    type: 'USER_LOGOUT',
  });
  
  // Force clear any remaining persisted state
  localStorage.removeItem('persist:root');
  sessionStorage.clear();
  
  console.log("Logout action completed");
};

// Get current user
export const getCurrentUser = () => async (dispatch) => {
  try {
    dispatch({ type: 'USER_GET_REQUEST' });
    
    const user = await UserService.getCurrentUser();
    
    dispatch({
      type: 'USER_GET_SUCCESS',
      payload: user
    });
  } catch (error) {
    dispatch({
      type: 'USER_GET_FAIL',
      payload: error.message
    });
    // If getting user fails, logout
    dispatch(logoutUser());
  }
};

// Update user profile
export const updateUserProfile = (profileData) => async (dispatch) => {
  try {
    dispatch({ type: 'USER_UPDATE_REQUEST' });
    
    const updatedUser = await UserService.updateProfile(profileData);
    
    dispatch({
      type: 'USER_UPDATE_SUCCESS',
      payload: updatedUser
    });
    
    // Show success toast
    if (window.toast) {
      window.toast.success('Profile updated successfully', 4000);
    }
    
    return { success: true };
  } catch (error) {
    let errorMessage = error.message;
    
    // Try to parse structured error responses
    try {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorData.errors.forEach((err, index) => {
            if (err.message || err.msg) {
              setTimeout(() => {
                if (window.toast) {
                  window.toast.error(err.message || err.msg, 6000);
                }
              }, index * 100);
            }
          });
          errorMessage = errorData.message || errorData.errors[0]?.message || error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
          if (window.toast) {
            window.toast.error(errorMessage, 6000);
          }
        }
      } else {
        if (window.toast) {
          window.toast.error(errorMessage || 'Failed to update profile. Please try again.', 6000);
        }
      }
    } catch (parseError) {
      console.warn('Could not parse error response:', parseError);
      if (window.toast) {
        window.toast.error('An unexpected error occurred. Please try again.', 6000);
      }
    }
    
    dispatch({
      type: 'USER_UPDATE_FAIL',
      payload: errorMessage
    });
    throw error;
  }
};

// Complete onboarding
export const completeOnboarding = (onboardingData) => async (dispatch) => {
  try {
    dispatch({ type: 'USER_UPDATE_REQUEST' });
    
    const updatedUser = await UserService.completeOnboarding(onboardingData);
    
    dispatch({
      type: 'USER_UPDATE_SUCCESS',
      payload: updatedUser
    });
    
    // Show success toast
    if (window.toast) {
      window.toast.success('Onboarding completed successfully!', 4000);
    }
    
    return { success: true };
  } catch (error) {
    let errorMessage = error.message;
    
    // Try to parse structured error responses
    try {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorData.errors.forEach((err, index) => {
            if (err.message || err.msg) {
              setTimeout(() => {
                if (window.toast) {
                  window.toast.error(err.message || err.msg, 6000);
                }
              }, index * 100);
            }
          });
          errorMessage = errorData.message || errorData.errors[0]?.message || error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
          if (window.toast) {
            window.toast.error(errorMessage, 6000);
          }
        }
      } else {
        if (window.toast) {
          window.toast.error(errorMessage || 'Failed to complete onboarding. Please try again.', 6000);
        }
      }
    } catch (parseError) {
      console.warn('Could not parse error response:', parseError);
      if (window.toast) {
        window.toast.error('An unexpected error occurred. Please try again.', 6000);
      }
    }
    
    dispatch({
      type: 'USER_UPDATE_FAIL',
      payload: errorMessage
    });
    throw error;
  }
};

// Verify OTP
export const verifyOTP = (otpData) => async (dispatch) => {
  try {
    dispatch({ type: 'USER_VERIFY_OTP_REQUEST' });
    
    const response = await UserService.verifyOTP(otpData);
    
    dispatch({
      type: 'USER_VERIFY_OTP_SUCCESS',
      payload: {
        token: response.token,
        user: response.user
      }
    });
    
    // Show success toast
    if (window.toast) {
      window.toast.success('Email verified successfully!', 4000);
    }
    
    return { success: true };
  } catch (error) {
    let errorMessage = error.message;
    
    // Try to parse structured error responses
    try {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorData.errors.forEach((err, index) => {
            if (err.message || err.msg) {
              setTimeout(() => {
                if (window.toast) {
                  window.toast.error(err.message || err.msg, 6000);
                }
              }, index * 100);
            }
          });
          errorMessage = errorData.message || errorData.errors[0]?.message || error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
          if (window.toast) {
            window.toast.error(errorMessage, 6000);
          }
        }
      } else {
        if (window.toast) {
          window.toast.error(errorMessage || 'Failed to verify OTP. Please try again.', 6000);
        }
      }
    } catch (parseError) {
      console.warn('Could not parse error response:', parseError);
      if (window.toast) {
        window.toast.error('An unexpected error occurred. Please try again.', 6000);
      }
    }
    
    dispatch({
      type: 'USER_VERIFY_OTP_FAIL',
      payload: errorMessage
    });
    throw error;
  }
};

// XP Actions
export const awardXP = (eventType, metadata = {}) => async (dispatch) => {
  try {
    dispatch({ type: 'AWARD_XP_REQUEST' });
    
    const response = await UserService.awardXP(eventType, metadata);
    
    dispatch({
      type: 'AWARD_XP_SUCCESS',
      payload: response
    });
    
    return response;
  } catch (error) {
    // Safe check for error.response existence before accessing
    const errorMessage = (error.response && error.response.data && error.response.data.error) 
      ? error.response.data.error 
      : error.message || 'Failed to award XP';
    
    dispatch({
      type: 'AWARD_XP_FAIL',
      payload: errorMessage
    });
    throw error;
  }
};

export const getXPProfile = () => async (dispatch) => {
  try {
    dispatch({ type: 'FETCH_XP_PROFILE_REQUEST' });
    
    const response = await UserService.getXPProfile();
    
    dispatch({
      type: 'FETCH_XP_PROFILE_SUCCESS',
      payload: response.data
    });
    
    return response;
  } catch (error) {
    // Safe check for error.response existence before accessing
    const errorMessage = (error.response && error.response.data && error.response.data.error) 
      ? error.response.data.error 
      : error.message || 'Failed to get XP profile';
    
    dispatch({
      type: 'FETCH_XP_PROFILE_FAIL',
      payload: errorMessage
    });
    throw error;
  }
};

export const getXPHistory = (page = 1, limit = 20) => async (dispatch) => {
  try {
    dispatch({ type: 'FETCH_XP_HISTORY_REQUEST' });
    
    const response = await UserService.getXPHistory(page, limit);
    
    dispatch({
      type: 'FETCH_XP_HISTORY_SUCCESS',
      payload: response.data
    });
    
    return response;
  } catch (error) {
    // Safe check for error.response existence before accessing
    const errorMessage = (error.response && error.response.data && error.response.data.error) 
      ? error.response.data.error 
      : error.message || 'Failed to get XP history';
    
    dispatch({
      type: 'FETCH_XP_HISTORY_FAIL',
      payload: errorMessage
    });
    throw error;
  }
};

export const updateUserXP = (xpData) => ({
  type: 'UPDATE_USER_XP',
  payload: xpData
});
